import React, { useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Backdrop, CircularProgress, useTheme } from "@mui/material";
import axios from "axios";
import backend from "../../../../../api";
import { setUserData } from "../../../../../redux-store/auth/actions";
import Classes from "./Profile.module.css";

import BasicInfo from "./Comp/BasicInfo";
import Goals from "./Comp/Goals";
import Preferences from "./Comp/Preferences";
import Extras from "./Comp/Extras";
import {
  ProfileIcon,
  GoalIcon,
} from "../../../../../utils/Icons";
import { enqueueSnackbar } from "notistack";

export default function Information() {
  const { user } = useSelector((s) => s.auth);
  const dispatch = useDispatch();
  const theme = useTheme();

  const [saving, setSaving] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [error, setError] = useState(null);

  const sideBarSteps = [
    { id: 1, label: "Basic Info", icon: <ProfileIcon height="15" fill="white" /> },
    { id: 2, label: "Goals", icon: <GoalIcon height="17" fill="white" /> },
    { id: 3, label: "Preferences", icon: <img src="/assets/icons/session-details.png" height={17} width={17} /> },
    { id: 4, label: "Extras", icon: <img src="/assets/icons/extras.png" height={16} width={16} /> },
  ];

  /* ---------- save helper ---------- */
  const saveProfile = useCallback(
    async (patch) => {
      if (user.role !== "mentee") {
        setError("Only mentees can update mentee profile");
        return;
      }

      setSaving(true);
      setError(null);
      try {
        const payload = {
          menteeProfile: { ...user.menteeProfile, ...patch },
        };
        const { data } = await backend.put(`/api/user/${user._id}`, payload);
        dispatch(setUserData({ ...user, ...data.data }));
        enqueueSnackbar("Saved Successfully!", { variant: "success" });
      } catch (err) {
        console.error("Error saving profile:", err.response?.data || err.message);
        setError(err.response?.data?.error || "Failed to save profile");
      } finally {
        setSaving(false);
      }
    },
    [dispatch, user]
  );

  /* ---------- upload helper ---------- */
  const uploadMedia = async (files) => {
    setSaving(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const res = await axios.post(
        "https://mentr-aws-server-668d71936193.herokuapp.com/api/deck/upload",
        fd
      );
      console.log("Uploaded file URL:", res.data.data[0]?.imgurl);
      return res.data.data[0]?.imgurl;
    } catch (err) {
      console.error("Error uploading file:", err.response?.data || err.message);
      setError(err.response?.data?.error || "Failed to upload file");
      return null;
    } finally {
      setSaving(false);
    }
  };

  /* ---------- JSX ---------- */
  const renderStepComponent = () => {
    switch (activePage) {
      case 1:
        return (
          <BasicInfo
            initial={user?.menteeProfile?.basicInfo}
            uploadMedia={uploadMedia}
            saveProfile={saveProfile}
          />
        );
      case 2:
        return (
          <Goals
            initial={user?.menteeProfile?.goalsAndInterests}
            saveProfile={saveProfile}
          />
        );
      case 3:
        return (
          <Preferences
            initial={user?.menteeProfile?.preferences}
            saveProfile={saveProfile}
          />
        );
      case 4:
        return (
          <Extras
            initial={user?.menteeProfile?.extras}
            uploadMedia={uploadMedia}
            saveProfile={saveProfile}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Backdrop
        open={saving}
        sx={{
          zIndex: theme.zIndex.tooltip + 1,
          color: "#fff",
          backdropFilter: "blur(2px)",
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <div className={Classes.container}>
        <div className={Classes.header}>
          <div>
            <h3 className={Classes.title3}>Profile</h3>
            <h5 className={Classes.text}>Manage your mentee profile.</h5>
          </div>
        </div>

     
        <div className={Classes.hero}>
          {/* sidebar */}
          <div className={Classes.sidebar}>
            {sideBarSteps.map((s) => (
              <div
                key={s.id}
                role="button"
                onClick={() => setActivePage(s.id)}
                className={`${Classes.sideElement} ${activePage === s.id ? Classes.current : ""
                  }`}
              >
                <div className={Classes.flexAlign10}>
                  <div
                    className={`${Classes.imageCon} ${activePage === s.id ? Classes.active : ""
                      }`}
                  >
                    {s.icon}
                  </div>
                  <h5
                    className={
                      activePage === s.id ? Classes.activeText : undefined
                    }
                  >
                    {s.label}
                  </h5>
                </div>
              </div>
            ))}
          </div>

          {/* main */}
          {renderStepComponent()}
        </div>
      </div>
    </>
  );
}